import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines, FadeIn } from "@/components/Motion";
import { SERVICES, VALUES, CEO, IMAGES, TECH_IMAGES } from "@/data/site";

const MARQUEE_ITEMS = [
  "Strategic Management",
  "Financial Advisory",
  "Business Software Reselling",
  "Sage 300 ERP",
  "ERP Implementation",
  "Audit & Accounting",
  "Tax Services",
  "Business Software Training",
  "Corporate Governance",
  "Sustainable Growth",
];

function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-navy-deep"
      data-testid="home-hero"
    >
      <motion.img
        src={IMAGES.hero}
        alt="Aerial view of modern corporate towers in Lagos, Nigeria"
        className="absolute inset-0 h-full w-full object-cover"
        style={reduce ? undefined : { y, scale: 1.12 }}
        loading="eager"
        fetchPriority="high"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-navy-deep/90 via-navy-deep/55 to-navy-deep"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-r from-navy-deep/80 via-transparent to-transparent"
        aria-hidden="true"
      />
      <div className="grain absolute inset-0" aria-hidden="true" />

      <div className="container-x relative z-10 pb-20 pt-40 sm:pb-24 lg:pb-28">
        <FadeIn delay={0.15} y={16}>
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            Management Consultancy &amp; Business Software — Est. 2002, Nigeria
          </p>
        </FadeIn>

        <h1 className="mt-7 font-serif text-[clamp(2.6rem,7.2vw,6rem)] font-medium leading-[1.05] text-white">
          <MaskedLines
            delay={0.3}
            lines={[
              <span key="l1">Empowering Businesses.</span>,
              <span key="l2">
                Driving{" "}
                <em className="font-medium italic text-gold">Sustainable Growth.</em>
              </span>,
            ]}
          />
        </h1>

        <FadeIn delay={1.0}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
            Since 2002, we have helped organizations navigate business
            complexity through professional management consulting, financial
            expertise, enterprise software solutions, and technology-driven
            transformation.
          </p>
        </FadeIn>

        <FadeIn delay={1.15} className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/contact" data-testid="hero-cta-schedule" className="btn-gold">
            Schedule a Consultation
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-services"
            className="btn-outline-light"
          >
            Explore Our Services
          </Link>
        </FadeIn>
      </div>

      <motion.div
        aria-hidden="true"
        className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 lg:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <motion.span
          className="h-14 w-px origin-top bg-gradient-to-b from-gold to-transparent"
          animate={reduce ? undefined : { scaleY: [1, 0.25, 1] }}
          transition={reduce ? undefined : { repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}

function Intro() {
  return (
    <section className="bg-white py-20 lg:py-32" data-testid="home-intro">
      <div className="container-x grid items-start gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            Introduction
          </p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl lg:text-[2.9rem]">
            Your Trusted Partner in Business Excellence
          </h2>
          <span aria-hidden="true" className="mt-8 block h-px w-20 bg-gold" />
          <p className="mt-8 text-lg leading-relaxed text-charcoal/80">
            Established in 2002, Steven Philips &amp; Associates Limited (SPA)
            is a Nigerian professional services firm providing management
            consultancy, accounting and financial advisory, and enterprise
            software solutions.
          </p>
          <p className="mt-5 text-base leading-relaxed text-charcoal/70">
            We help organizations improve operational efficiency, strengthen
            financial management, and implement technology solutions that
            support sustainable business growth — serving clients across
            Nigeria and the wider West African market.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <figure className="group relative">
            <span
              aria-hidden="true"
              className="absolute -right-4 -top-4 hidden h-full w-full border border-gold/40 sm:block"
            />
            <div className="relative overflow-hidden">
              <img
                src={IMAGES.homeIntro}
                alt="Business executives collaborating during an advisory session"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
            </div>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section className="bg-mist py-20 lg:py-28" data-testid="home-pillars">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow">Core Value Proposition</p>
        </Reveal>
        <div className="mt-12 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.number} delay={i * 0.1} className="bg-mist">
              <div className="group flex h-full flex-col bg-mist p-8 transition-colors duration-500 hover:bg-white lg:p-10">
                <span
                  aria-hidden="true"
                  className="h-px w-8 bg-gold transition-all duration-500 group-hover:w-16"
                />
                <span className="mt-8 font-serif text-lg text-gold">{v.number}</span>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-navy">
                  {v.title}
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-charcoal/70">
                  {v.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Expertise() {
  const erp = SERVICES.find((s) => s.group === "technology");
  const advisory = SERVICES.filter((s) => s.group === "advisory");
  return (
    <section className="bg-white py-20 lg:py-32" data-testid="home-expertise">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <p className="eyebrow flex items-center gap-4">
            <span aria-hidden="true" className="h-px w-10 bg-gold" />
            Our Expertise
          </p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl lg:text-[2.9rem]">
            Enterprise Software at the Core. Professional Services Behind It.
          </h2>
        </Reveal>

        <Reveal className="mt-14">
          <Link
            to={`/services#${erp.anchor}`}
            data-testid="home-expertise-erp"
            className="group relative block overflow-hidden bg-navy-deep"
          >
            <div className="grain absolute inset-0" aria-hidden="true" />
            <div className="relative grid items-stretch lg:grid-cols-[1.15fr_1fr]">
              <div className="p-8 sm:p-12 lg:p-14">
                <p className="eyebrow-navy">Core Service — Business Software &amp; ERP Solutions</p>
                <h3 className="mt-5 font-serif text-2xl font-semibold leading-[1.15] text-white sm:text-3xl lg:text-[2.4rem]">
                  {erp.title}
                </h3>
                <p className="mt-4 text-[11px] font-semibold uppercase leading-[1.9] tracking-[0.22em] text-gold">
                  {erp.subheading}
                </p>
                <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/60 sm:text-[15px]">
                  More than two decades of experience in business software
                  reselling, Sage 300 ERP implementation, customization,
                  training, and technical support — SPA's core service since
                  2002.
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                  Explore ERP Solutions
                  <ArrowUpRight
                    aria-hidden="true"
                    className="h-4 w-4 text-gold transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
                  />
                </span>
              </div>
              <div className="relative min-h-[240px] overflow-hidden lg:min-h-0">
                <img
                  src={erp.image}
                  alt={erp.alt}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  loading="lazy"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-t from-navy-deep/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-navy-deep lg:via-navy-deep/20 lg:to-transparent"
                />
              </div>
            </div>
          </Link>
        </Reveal>

        <Reveal className="mt-16">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-charcoal/45">
            Professional &amp; Financial Advisory
          </p>
        </Reveal>
        <div className="mt-5 border-t border-navy/10">
          {advisory.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.04}>
              <Link
                to={`/services#${s.anchor}`}
                data-testid={`home-expertise-${s.id}`}
                className="group -mx-2 grid grid-cols-[auto_1fr_auto] items-center gap-5 border-b border-navy/10 px-2 py-6 transition-all duration-400 hover:bg-navy sm:gap-8 sm:px-6 sm:py-7"
              >
                <span className="font-serif text-base text-gold sm:text-lg">
                  {s.number}
                </span>
                <span className="block">
                  <span className="block font-serif text-xl font-semibold text-navy transition-colors duration-300 group-hover:text-white sm:text-2xl">
                    {s.title}
                  </span>
                  <span className="mt-1.5 hidden max-w-xl text-sm leading-relaxed text-charcoal/60 transition-colors duration-300 group-hover:text-white/60 sm:block">
                    {s.homeDescription}
                  </span>
                </span>
                <ArrowUpRight
                  aria-hidden="true"
                  className="h-5 w-5 text-navy/25 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-gold sm:h-6 sm:w-6"
                />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SageStrip() {
  return (
    <section className="bg-mist py-20 lg:py-28" data-testid="home-sage-strip">
      <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <figure className="group relative">
            <span
              aria-hidden="true"
              className="absolute -right-4 -top-4 hidden h-full w-full border border-gold/40 sm:block"
            />
            <div className="relative overflow-hidden">
              <img
                src={TECH_IMAGES.infrastructure}
                alt="Modern enterprise technology infrastructure in a data centre"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
            </div>
          </figure>
        </Reveal>
        <Reveal delay={0.12}>
          <div>
            <p className="eyebrow flex items-center gap-4">
              <span aria-hidden="true" className="h-px w-10 bg-gold" />
              Enterprise Software
            </p>
            <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl">
              Sage 300 ERP, Implemented Right.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-charcoal/70 sm:text-lg">
              With more than two decades of experience in business software
              reselling and ERP implementation, SPA guides organizations
              through Sage 300 ERP (formerly Sage Accpac ERP) selection,
              implementation, customization, training, and long-term technical
              support — so your systems work the way your business does.
            </p>
            <Link
              to="/services#erp-solutions"
              data-testid="home-sage-cta"
              className="btn-navy mt-10"
            >
              Explore ERP Solutions
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function LeadershipPreview() {
  return (
    <section
      className="relative overflow-hidden bg-mist py-20 lg:py-32"
      data-testid="home-leadership"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 select-none font-serif text-[11rem] leading-none text-gold/15"
      >
        &ldquo;
      </span>
      <div className="container-x relative max-w-3xl text-center">
        <Reveal>
          <p className="eyebrow justify-center">Leadership</p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-[1.14] text-navy sm:text-4xl lg:text-[2.9rem]">
            Leadership Grounded in Experience and Integrity
          </h2>
          <span aria-hidden="true" className="mx-auto mt-8 block h-px w-20 bg-gold" />
          <h3 className="mt-10 font-serif text-2xl font-semibold text-navy sm:text-[2rem]">
            {CEO.name}
          </h3>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">
            {CEO.position}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-base leading-relaxed text-charcoal/75 sm:text-lg">
            Stephen Irebia is a seasoned accounting and financial
            management professional with over 30 years of experience spanning
            audit, corporate advisory, financial oversight, strategic management,
            and organizational leadership.
          </p>
          <p className="mt-5 text-base leading-relaxed text-charcoal/75 sm:text-lg">
            As Managing Director and Chief Executive Officer of Steven Philips
            &amp; Associates Limited, he brings extensive professional experience
            and a strong commitment to integrity, accountability, and sustainable
            organizational growth.
          </p>
          <Link
            to="/about"
            data-testid="home-leadership-cta"
            className="btn-navy mt-10"
          >
            Meet Our Leadership
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      className="relative overflow-hidden bg-navy-deep"
      data-testid="home-final-cta"
    >
      <img
        src={IMAGES.hero}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-[0.12]"
        loading="lazy"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-navy-deep via-navy-deep/70 to-navy-deep"
        aria-hidden="true"
      />
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="container-x relative max-w-3xl py-24 text-center lg:py-36">
        <Reveal>
          <p className="eyebrow-navy justify-center">Begin the Conversation</p>
          <h2 className="mt-6 font-serif text-4xl font-semibold leading-[1.12] text-white sm:text-5xl">
            Your Next Chapter of Growth Starts Here.
          </h2>
          <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            Partner with experienced professionals committed to helping your
            organization navigate complexity, strengthen performance, and unlock
            lasting value.
          </p>
          <Link
            to="/contact"
            data-testid="home-final-cta-button"
            className="btn-gold mt-10"
          >
            Schedule a Consultation
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Business Software Reselling & ERP Implementation in Nigeria | Steven Philips & Associates Limited"
        description="Established in 2002, SPA resells, implements, customizes, and supports business software including Sage 300 ERP (formerly Sage Accpac) — alongside management consultancy, accounting, auditing, tax and financial services in Nigeria."
      />
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Intro />
      <Pillars />
      <Expertise />
      <SageStrip />
      <LeadershipPreview />
      <FinalCta />
    </>
  );
}
